import { STRIPE_MAX_AMOUNT } from '@repo/lib';
import { z } from '../zod.js';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId.js';
import { SupportedCurrencyEnum, CouponDurationEnum } from './enum.js';
import {
  CouponRuleInputValidationSchema,
  couponPageIdsDescription,
  couponRulesDescription,
  couponTicketTypeIdsDescription,
  rulesWithoutLegacyScope,
} from './scopeDescriptions.js';

const labelOpenapi = {
  description: 'Internal name for the coupon.',
  example: 'Free shipping within US',
};

const couponOpenapi = {
  description: 'The coupon code a customer enters.',
  example: '10Off',
};

const CouponDurationValidationSchema = z.enum(['once', 'repeating', 'forever']).meta({
  description:
    'How long the discount lasts: `once` applies to the first eligible payment, `repeating` applies for a fixed number of months, and `forever` applies until canceled or expired.',
});

export const CreateCouponInputValidationSchema = z
  .object({
    label: z.string().min(1, 'Label is required').meta(labelOpenapi),
    code: z
      .string()
      .min(1, 'Code is required')
      .regex(/^[A-Za-z0-9_-]+$/, 'Code can only contain letters, numbers, underscores, and dashes')
      .meta(couponOpenapi),
    amountOff: z.number().min(1).max(STRIPE_MAX_AMOUNT).optional().meta({
      example: 100,
      description:
        'A positive integer representing the amount to subtract from an invoice total, in the smallest currency unit of `currency` (for example `500` USD is $5.00 and `500` JPY is ¥500). Required if `percentOff` is not passed.',
    }),
    currency: SupportedCurrencyEnum.optional().meta({
      description:
        'Three-letter ISO code for the currency of the amountOff parameter. Required when `amountOff` is set, and not allowed when `percentOff` is set.',
      example: 'usd',
    }),
    duration: CouponDurationValidationSchema,
    durationInMonths: z.number().positive().optional().meta({
      description:
        'A positive integer representing the number of months the coupon applies for. Required when `duration === repeating`, and not allowed when `duration` is `once` or `forever`.',
      example: 3,
    }),
    percentOff: z.number().min(1).max(100).optional().meta({
      description:
        'A positive float larger than 0, and smaller or equal to 100, that represents the discount the coupon will apply (required if `amountOff` is not passed).',
    }),
    appliesToSetupFee: z.boolean().optional().meta({
      description: 'When enabled, applies this coupon discount to the setup fee.',
      example: true,
    }),
    appliesToTicketFees: z.boolean().optional().meta({
      description:
        'When enabled, applies this coupon discount to ticket-level fees on event pages as well as the ticket prices. Defaults to false, which leaves ticket fees undiscounted.',
      example: false,
    }),

    rules: z
      .array(CouponRuleInputValidationSchema)
      .optional()
      .meta({
        description: couponRulesDescription,
        example: [
          {
            pageId: '67ee075004de439ab0b675b6',
            appliesTo: 'pricesAndBumps',
            priceIds: ['67fcbdac6a91c25ef2d3534a'],
            bumpProductIds: [],
          },
        ],
      }),
    pageIds: z
      .array(ObjectIdStringValidationSchema)
      .optional()
      .meta({
        description: couponPageIdsDescription,
        deprecated: true,
        example: ['67ee075004de439ab0b675b6', '67fcbdac6a91c25ef2d3534a'],
      }),
    ticketTypeIds: z
      .array(ObjectIdStringValidationSchema)
      .optional()
      .meta({
        description: couponTicketTypeIdsDescription,
        deprecated: true,
        example: ['67ee075004de439ab0b675b6', '67fcbdac6a91c25ef2d3534a'],
      }),
    maxRedemptions: z.number().min(0).optional().meta({
      description:
        "The total number of times this coupon can be redeemed across all customers. Doesn't limit a single customer from redeeming multiple times. Omit for unlimited redemptions.",
      example: 10,
    }),
    redeemBy: z.iso.datetime().optional().meta({
      description: 'The ISO datetime that this coupon must be used before.',
      example: '2025-10-04T07:36:45.122Z',
    }),
  })
  .refine(data => data.amountOff || data.percentOff, {
    message: 'Either amountOff or percentOff must be specified',
    path: ['amountOff'],
  })
  .refine(data => !(data.amountOff && data.percentOff), {
    message: 'Cannot specify both amountOff and percentOff',
    path: ['percentOff'],
  })
  .refine(data => !data.amountOff || data.currency, {
    message: 'Currency is required when amountOff is specified',
    path: ['currency'],
  })
  .refine(data => !data.percentOff || !data.currency, {
    message: 'currency is only allowed when amountOff is specified',
    path: ['currency'],
  })
  .refine(data => data.duration !== 'repeating' || data.durationInMonths, {
    message: 'durationInMonths is required when duration is repeating',
    path: ['durationInMonths'],
  })
  .refine(rulesWithoutLegacyScope.check, {
    message: rulesWithoutLegacyScope.message,
    path: ['rules'],
  })
  .refine(data => data.duration === 'repeating' || !data.durationInMonths, {
    message: 'durationInMonths is only allowed when duration is repeating',
    path: ['durationInMonths'],
  });
